import { useEffect, useMemo, useState } from "react";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { saveAs } from "file-saver";
import { getRentRoll, exportRentRollExcel } from "../TenantForm/Helpers/api";
import { formatDay, formatMoney, safeFileName, todayYmd } from "./reportFormat";
import "../portfolioTable.css";
import "./rentRoll.css";

/** The expanded roll's column categories — ids match the server's RENT_ROLL_CATEGORIES. */
export const RENT_ROLL_CATEGORIES = [
  { id: "unit", label: "Unit" },
  { id: "residents", label: "Residents" },
  { id: "term", label: "Term" },
  { id: "deposit", label: "Security Deposit" },
  { id: "target", label: "Target Rent" },
  { id: "rent", label: "Monthly Rent" },
  { id: "insurance", label: "Insurance" },
  { id: "utilities", label: "Utilities" },
  { id: "details", label: "Other Lease Details" },
  { id: "future", label: "Future Lease" },
];

/** What the expanded roll opens with: the money, without the long text columns. */
const DEFAULT_CATEGORIES = ["residents", "term", "deposit", "target", "rent", "insurance"];

const Kpi = ({ label, value, sub, tone = "" }) => (
  <div className={`pft-stat ${tone}`.trim()}>
    <div className="pft-stat__label">{label}</div>
    <div className="pft-stat__value">{value}</div>
    {sub ? <div className="pft-stat__sub">{sub}</div> : null}
  </div>
);

/** Runs of neighbouring columns that share a category, for the band above the header. */
export const bandsOf = (columns) => {
  const bands = [];
  for (const c of columns) {
    const last = bands[bands.length - 1];
    if (last && last.label === c.groupLabel) last.span += 1;
    else bands.push({ label: c.groupLabel, span: 1 });
  }
  return bands;
};

const Cell = ({ column, value }) => {
  if (value === null || value === undefined || value === "") return <span className="rr-muted">—</span>;
  if (column.type === "date") return formatDay(value);
  if (column.type === "money") {
    // The sign carries the meaning; the colour only repeats it.
    if (column.id === "variance" && Number(value) !== 0) {
      return <span className={Number(value) < 0 ? "rr-neg" : "rr-pos"}>{Number(value) > 0 ? "+" : ""}{formatMoney(value)}</span>;
    }
    return formatMoney(value);
  }
  return value;
};

/**
 * The Rent Roll (CFO, 2026-10-06): one row per unit for this building on a date. Simplified
 * is his fixed sheet; Expanded lets the reader pick the column categories. The server builds
 * both from one column list, so this table only draws what it is sent.
 */
const RentRollTab = ({ masterSheetData }) => {
  const mastersheetId = masterSheetData?._id;
  const [date, setDate] = useState(todayYmd());
  const [view, setView] = useState("simple");
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const params = useMemo(
    () => ({ date, view, ...(view === "expanded" ? { categories: categories.join(",") || "none" } : {}) }),
    [date, view, categories]
  );

  useEffect(() => {
    let active = true;
    if (!mastersheetId || !date) return undefined;
    setLoading(true);
    setError(null);
    getRentRoll(mastersheetId, params)
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load the rent roll");
        setReport(resp.data);
      })
      .catch((e) => active && setError(e?.response?.data?.message || e.message || "Failed to load the rent roll"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [mastersheetId, date, params]);

  const toggleCategory = (id) =>
    setCategories((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await exportRentRollExcel(mastersheetId, params);
      saveAs(blob, `RentRoll_${safeFileName(masterSheetData?.building_name)}_${date}.xlsx`);
    } catch (e) {
      setError("Could not download the Excel file. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const columns = report?.columns || [];
  const bands = bandsOf(columns);
  const hasBand = bands.some((b) => b.label);
  const t = report?.totals || {};

  return (
    <div data-testid="rent-roll">
      <div className="rr-controls">
        <div>
          <label htmlFor="rr-date">As of</label>
          <input
            id="rr-date" type="date" className="form-control" value={date} max={todayYmd()}
            onChange={(e) => e.target.value && setDate(e.target.value)}
          />
        </div>
        <div>
          <label id="rr-view-label">Report</label>
          <div className="btn-group btn-group-sm" role="group" aria-labelledby="rr-view-label">
            {[["simple", "Simplified"], ["expanded", "Expanded"]].map(([id, label]) => (
              <button
                key={id} type="button" aria-pressed={view === id}
                className={`btn ${view === id ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setView(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="rr-controls__end">
          {loading && report ? <Spinner size="sm" color="primary" aria-label="Refreshing" /> : null}
          <button type="button" className="rpt-action rpt-action--primary" onClick={handleDownload} disabled={downloading || !report}>
            {downloading ? <Spinner size="sm" /> : <><FeatherIcon icon="download" size={14} /> Download Excel</>}
          </button>
        </div>
      </div>

      {view === "expanded" && (
        <fieldset className="rr-chips" aria-label="Column categories">
          {RENT_ROLL_CATEGORIES.map((c) => (
            <label className="rr-chip" key={c.id}>
              <input type="checkbox" checked={categories.includes(c.id)} onChange={() => toggleCategory(c.id)} />
              <span>{c.label}</span>
            </label>
          ))}
          <button
            type="button" className="rr-chips__all"
            onClick={() => setCategories(categories.length === RENT_ROLL_CATEGORIES.length ? [] : RENT_ROLL_CATEGORIES.map((c) => c.id))}
          >
            {categories.length === RENT_ROLL_CATEGORIES.length ? "Clear all" : "Select all"}
          </button>
        </fieldset>
      )}

      {error ? <p className="text-danger" role="alert">{error}</p> : null}
      {loading && !report ? <div className="text-center p-4"><Spinner color="primary" /> Building the rent roll…</div> : null}

      {report && (
        <>
          <div className="pft-stats" data-testid="rent-roll-kpis">
            <Kpi label="Units" value={report.counts.units} sub={`${report.counts.occupied} occupied · ${report.counts.vacant} vacant`} />
            {t.rentTotal !== undefined && <Kpi label="Monthly rent" value={formatMoney(t.rentTotal)} sub="occupied units, all fees" tone="pft-stat--occupied" />}
            {t.targetTotal !== undefined && <Kpi label="Target rent" value={formatMoney(t.targetTotal)} sub="every unit" />}
            {t.variance !== undefined && (
              <Kpi
                label="Variance to target"
                value={<span className={t.variance < 0 ? "rr-neg" : ""}>{t.variance > 0 ? "+" : ""}{formatMoney(t.variance)}</span>}
                sub="occupied units, like for like"
              />
            )}
            {t.depositRefundable !== undefined && <Kpi label="Refundable deposits" value={formatMoney(t.depositRefundable)} sub="occupied units" />}
          </div>

          <div className="rr-scroll" tabIndex={0} role="region" aria-label={`Rent roll as of ${formatDay(report.asOfDate)}`}>
            <table className="rr-table">
              <thead>
                {hasBand && (
                  <tr className="rr-band">
                    {bands.map((b, i) => (
                      <th key={`${b.label}-${i}`} colSpan={b.span} scope="colgroup" className={`${b.label ? "" : "is-empty"} ${i === 0 ? "rr-pin" : ""}`.trim()}>
                        {b.label}
                      </th>
                    ))}
                  </tr>
                )}
                <tr className={hasBand ? "rr-head" : "rr-head rr-head--solo"}>
                  {columns.map((c, i) => (
                    <th key={c.id} scope="col" className={`${c.type === "money" || c.type === "number" ? "is-num" : ""} ${i === 0 ? "rr-pin" : ""}`.trim()}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.length ? report.rows.map((r) => (
                  <tr key={r.unitNo} className={r.tenants ? "" : "is-vacant"}>
                    {columns.map((c, i) => (
                      <td
                        key={c.id}
                        className={`${c.type === "money" || c.type === "number" ? "is-num" : ""} ${c.id === "comments" ? "is-wrap" : ""} ${i === 0 ? "rr-pin" : ""}`.trim()}
                      >
                        {c.id === "tenants" && !r.tenants ? "Vacant" : <Cell column={c} value={r[c.id]} />}
                      </td>
                    ))}
                  </tr>
                )) : (
                  <tr><td colSpan={columns.length} className="text-center rr-muted">This sheet has no units.</td></tr>
                )}
              </tbody>
              {report.rows.length > 0 && (
                <tfoot>
                  <tr>
                    {columns.map((c, i) => (
                      <td key={c.id} className={`${c.type === "money" ? "is-num" : ""} ${i === 0 ? "rr-pin" : ""}`.trim()}>
                        {i === 0 ? "Total" : c.type === "money" ? formatMoney(t[c.id]) : ""}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <p className="rr-note">
            Variance compares basic rent, AC and telecom with their targets; parking, pet, storage and extra-tenant
            fees have no target. Original Lease Start is blank where the tenancy began before RDCare's records.
            {report.asOfDate < todayYmd() ? " Unit rows are shown as they stood on the chosen date." : ""}
          </p>
        </>
      )}
    </div>
  );
};

export { RentRollTab };
